import { PersonalItemBaseDirective } from '$core/base/_personal-item.component';
import { ConfirmationModalComponent } from '$core/modal/confirmation-modal/confirmation-modal.component';
import dateRangeMap from '$core/models/date-range-map';
import { Basket } from '$core/models/subscription-order';
import { AuthenticationService } from '$core/services/auth.service';
import { ConfigService } from '$core/services/config.service';
import { SessionService } from '$core/services/session.service';
import { SwalService } from '$core/shell/swal/swal.service';
import { CartService } from '$modules/cart/cart.service';
import { DeliveryTypes } from '$modules/pages/subscriptions/show-subscription/delivery-types';
import { transition, trigger, useAnimation } from '@angular/animations';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { fadeIn, fadeOut } from 'ng-animate';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { debounceTime, first } from 'rxjs/operators';

@Component({
  selector: 'app-cart-item',
  templateUrl: './cart-item.component.html',
  styleUrls: ['./cart-item.component.scss'],
  animations: [
    trigger('fadeIn', [
      transition(
        ':enter',
        useAnimation(fadeIn, {
          // Set the duration to 5seconds and delay to 2seconds
          params: { timing: 0.2 },
        })
      ),
    ]),
    trigger('fadeOut', [
      transition(
        ':leave',
        useAnimation(fadeOut, {
          // Set the duration to 5seconds and delay to 2seconds
          params: { timing: 0.2 },
        })
      ),
    ]),
  ],
})
export class CartItemComponent
  extends PersonalItemBaseDirective<Basket>
  implements OnInit
{
  public type = 'CART_ITEM';
  public form: FormGroup;
  private updateStream = new Subject();
  public periods;
  public canSave = true;
  public link = faExternalLinkAlt;
  public url;
  public map = dateRangeMap;
  public deliveryMap = {
    address: 'TO_SUBSCRIBER_ADDRESS',
    ops_box: 'SUBSCRIPTION_BOX',
    ops: 'POSTE_RESTANTE',
    virtual: 'VIRTUAL_SUBSCRIPTION',
  };

  public get isLegal(): boolean {
    return this.sessionService.session
      ? this.sessionService.session.type === 'legal'
      : false;
  }

  constructor(
    public authService: AuthenticationService,
    protected requestService: CartService,
    protected fb: FormBuilder,
    protected modalService: BsModalService,
    protected swalService: SwalService,
    protected translates: TranslateService,
    protected configService: ConfigService,
    protected sessionService: SessionService
  ) {
    super(authService, requestService, modalService, swalService, translates);
  }

  public ngOnInit() {
    this.url = '/' + this.configService.urlByService('subscription');
    this.updateStream.pipe(debounceTime(350)).subscribe((item: Basket) => {
      if (!item.amount) {
        this.onDeleteItem(item);
      } else {
        this.saveItem.emit(item);
      }
    });

    this.form = this.fb.group({
      price: [this.item.amount],
    });
    this.form.get('price').valueChanges.subscribe((value) => {
      if (value !== this.item.amount) {
        const tmpP =
          this.item.price / (this.item.amount ? this.item.amount : 1);
        const tmpI =
          this.item.include_nds / (this.item.amount ? this.item.amount : 1);
        this.item.amount = value;
        this.item.price = (this.item.amount ? this.item.amount : 1) * tmpP;
        this.item.include_nds =
          (this.item.amount ? this.item.amount : 1) * tmpI;
        this.updateStream.next(this.item);
      }
    });
  }

  public toggleCollapsed(i) {
    super.toggleCollapsed(i);
    this.periods = undefined;
  }

  public delete(item): any {
    this.deleteItem.emit(item);
  }

  public onDeleteItem(item: Basket) {
    this.modalService.show(ConfirmationModalComponent, {
      class: 'modal-dialog-centered',
      initialState: {
        title: `DELETE_${this.type}_CONFIRMATION_QUESTION`,
        declineBtn: 'CANCEL',
      },
    });
    this.modalService.onHidden.pipe(first()).subscribe((res) => {
      if (res === 'confirm') {
        this.delete(item);
      } else {
        this.form.get('price').setValue(1);
        this.form.get('price').updateValueAndValidity();
      }
    });
  }

  public getTitle(arr: number[]) {
    return this.map[
      arr.length > 1
        ? arr[0].toString() + '-' + arr[arr.length - 1].toString()
        : arr[0].toString()
    ];
  }

  public changePeriod() {
    if (this.periods) {
      const months = [];
      this.periods.forEach((el) => {
        if (el.selected) {
          months.push(el.value);
        }
      });

      const tmpP = this.item.price / this.item.months.length;
      const tmpI = this.item.include_nds / this.item.months.length;
      this.item.months = months;

      this.item.price = this.item.months.length * tmpP;
      this.item.include_nds = this.item.months.length * tmpI;

      this.saveItem.emit(this.item);
      this.periods = undefined;
    } else {
      const startD = this.dateGenerator(this.item.basketable.start_at);
      const per = startD.getMonth() > 5 ? 'secondHalfYear' : 'firstHalfYear';
      let periods;

      switch (this.item.basketable.period) {
        case 1:
          periods = [...DeliveryTypes[per + 'Month']];
          break;
        case 2:
          periods = [...DeliveryTypes[per + 'DobleMonth']];
          break;
        case 3:
          periods = [...DeliveryTypes[per + 'Decade']];
          break;
        case 6:
          periods = [...DeliveryTypes[per + 'Year']];
          break;
      }
      this.parseAvalible(periods);
      this.periods = periods;
      this.checkCanSave();
    }
  }

  private parseAvalible(items) {
    // let selectedFirst = false;
    items.forEach((element, i) => {
      let disabled = false;
      element.value.forEach((month: number) => {
        if (this.item.basketable.without_months.indexOf(month) !== -1) {
          disabled = true;
        }
      });
      element.disabled = disabled;
      this.item.months.forEach((el) => {
        if (el.toString() === element.value.toString() && !disabled) {
          element.selected = true;
        }
      });
      // if (!disabled && !selectedFirst) {
      //   element.selected = true;
      //   selectedFirst = true;
      // }
    });
  }

  private dateGenerator(date: string): Date {
    const dateStartArr: any = date.match(/\d+/g);
    return new Date(
      dateStartArr[0],
      dateStartArr[1] - 1,
      dateStartArr[2],
      dateStartArr[3],
      dateStartArr[4],
      dateStartArr[5]
    );
  }

  public checkCanSave() {
    if (!this.periods) {
      return;
    }
    let cs = false;

    this.periods.forEach((el) => {
      if (el.selected) {
        cs = true;
      }
    });
    this.canSave = cs;
  }
}
